
// Force rebuild
import { AppAssessments } from "@/interfaces/data_objects/app_assessments";
import {
  APP_ENG_ASSESSMENT_COMPLETED,
  ASSESSMENT_QUESTIONNAIRES_ENGLISH,
  ASSESSMENT_QUESTIONNAIRES_ENGLISH_UPDATE,
  SUBMIT_APP_ASSESSMENT_ANSWERS
} from "@/store/modules/candidates/constants";
import { convert_seconds_to_minutes } from "@/utils/global";
import ErrorPage from "@/views/ErrorPage.vue";
import Vue from "vue";
import { mapActions, mapGetters, mapMutations } from "vuex";
// import AppTimer from "@/components/AppTimer.vue";
import AppTimer from "@/components/AppTimer.vue";
import {
  SubmitAssessmentAnswersApiPayload,
  UpdateAssessmentQuestionPayload
} from "@/store/modules/candidates/interfaces";
import { ROOT_ERROR } from "@/store/modules/root/constants";
import AppEngAssessmentHeader from "./AppEngAssessmentHeader.vue";
import AppEngWrittenAssessmentLoader from "./AppEngWrittenAssessmentLoader.vue";

export default Vue.extend({
  name: "AppEngWrittenAssessment",
  components: {
    ErrorPage,
    AppEngWrittenAssessmentLoader,
    AppEngAssessmentHeader,
    AppTimer
  },
  props: {
    assessment_type: { type: String, required: false, default: "english_v2" },
    restored_state: { type: Object, required: false, default: null }
  },
  data() {
    return {
      current_question_index: 0, // Current question index
      current_question: null as null | AppAssessments.AssessmentQuestionnaires, // Current question
      disabled_next_question: true, // Disable next question button
      current_question_answer: "", // Current question answer
      answer_words_count: 0, // Current question answer words count
      submit_ans_loading: false, // Submit answer loading
      answer_editor_options: {
        modules: {
          toolbar: [
            ["bold", "italic", "underline", "strike"],
            ["blockquote", "code-block"],
            [{ header: 1 }, { header: 2 }],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ script: "sub" }, { script: "super" }],
            [{ indent: "-1" }, { indent: "+1" }],
            [{ direction: "rtl" }],
            [{ size: ["small", false, "large", "huge"] }],
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            [{ font: [] }],
            [{ color: [] }, { background: [] }],
            [{ align: [] }],
            ["clean"],
            ["link"]
          ]
        },
        placeholder: "Type answer here",
        table: [] // Answer editor options
      }
    };
  },
  computed: {
    ...mapGetters("candidate", {
      app_eng_assessments: ASSESSMENT_QUESTIONNAIRES_ENGLISH
    }),
    AppAssessments() {
      return AppAssessments;
    },
    quill() {
      const quill = this.$refs?.answer_editor as {
        quill?: {
          getText: () => string;
          root: HTMLElement;
        };
      } | null;
      if (quill && quill.quill) return quill.quill;
      else return null;
    },
    getMinLimit(): number {
      if (
        this.current_question?.question_options?.allowed_sentences &&
        this.current_question.question_options.allowed_sentences.length > 0
      ) {
        return this.current_question.question_options.allowed_sentences[0] || 1;
      }
      const directive = this.parseDirective(
        String(this.current_question?.question_text || "")
      );
      if (directive) return directive.min;

      const questionNumber = this.current_question_index + 1;

      if (questionNumber === 2) {
        return 1;
      }
      if (questionNumber === 4) {
        return 4;
      }

      return (
        this.current_question?.question_options?.allowed_characters?.[0] || 0
      );
    },
    getMaxLimit(): number {
      if (
        this.current_question?.question_options?.allowed_sentences &&
        this.current_question.question_options.allowed_sentences.length > 0
      ) {
        return (
          this.current_question.question_options.allowed_sentences[1] ||
          this.current_question.question_options.allowed_sentences[0] ||
          1
        );
      }
      const directive = this.parseDirective(
        String(this.current_question?.question_text || "")
      );
      if (directive) return directive.max;

      const questionNumber = this.current_question_index + 1;

      if (questionNumber === 2) {
        return 2000;
      }
      if (questionNumber === 4) {
        return 5;
      }

      const charLimits =
        this.current_question?.question_options?.allowed_characters || [];
      return charLimits[1] || charLimits[0] || 0;
    },
    getLimitLabel(): string {
      if (
        this.current_question?.question_options?.allowed_sentences &&
        this.current_question.question_options.allowed_sentences.length > 0
      ) {
        return "Sentences";
      }
      const directive = this.parseDirective(
        String(this.current_question?.question_text || "")
      );
      if (directive)
        return directive.type === "sentences" ? "Sentences" : "Words";

      const questionNumber = this.current_question_index + 1;

      if (questionNumber === 2) {
        return "Words";
      }
      if (questionNumber === 4) {
        return "Sentences";
      }

      return "Words";
    }
  },
  watch: {
    // Watch current question answer and enable next button if answer editor exist
    // and answer editor text length is greater than 0 and less than max words/sentences allowed
    // for current question otherwise disable next button
    current_question_answer: {
      handler() {
        const ref = this.$refs.answer_editor as {
          quill?: { getText: () => string };
        } | null; // Answer editor ref
        // If answer editor not exist then disable next button
        if (!ref || !ref.quill) {
          this.disabled_next_question = true;
          return;
        }
        const text = ref.quill.getText().trim();
        const questionIndex = this.current_question_index;
        const questionNumber = questionIndex + 1;

        const hasAllowedSentences =
          this.current_question?.question_options?.allowed_sentences &&
          this.current_question.question_options.allowed_sentences.length > 0;
        if (hasAllowedSentences) {
          const sentences = text
            .split(/[.!?]+(?:\s+|$)/)
            .filter((s: string) => s.trim().length > 0);
          const sentenceCount = sentences.length;
          const allowedSentences =
            this.current_question?.question_options?.allowed_sentences;
          const min_sentences = (allowedSentences && allowedSentences[0]) || 1;
          const max_sentences =
            (allowedSentences && allowedSentences[1]) || min_sentences;
          this.answer_words_count = sentenceCount;
          this.disabled_next_question =
            sentenceCount < min_sentences || sentenceCount > max_sentences;
        } else {
          const directive = this.parseDirective(
            String(this.current_question?.question_text || "")
          );
          if (directive) {
            if (directive.type === "sentences") {
              const sentences = text
                .split(/[.!?]+(?:\s+|$)/)
                .filter((s: string) => s.trim().length > 0);
              const sentenceCount = sentences.length;
              this.answer_words_count = sentenceCount;
              this.disabled_next_question =
                sentenceCount < directive.min || sentenceCount > directive.max;
            } else {
              const words = text
                .split(/\s+/)
                .filter((w: string) => w.length > 0);
              const wordCount = words.length;
              this.answer_words_count = wordCount;
              this.disabled_next_question =
                wordCount < directive.min || wordCount > directive.max;
            }
          } else if (questionNumber === 2) {
            const words = text.split(/\s+/).filter((w: string) => w.length > 0);
            const wordCount = words.length;
            this.answer_words_count = wordCount;
            this.disabled_next_question = !(
              wordCount >= 1 && wordCount <= 2000
            );
          } else if (questionNumber === 4) {
            const sentences = text
              .split(/[.!?]+(?:\s+|$)/)
              .filter((s: string) => s.trim().length > 0);
            const sentenceCount = sentences.length;
            this.answer_words_count = sentenceCount;
            this.disabled_next_question = !(
              sentenceCount >= 4 && sentenceCount <= 5
            );
          } else {
            const words = text.split(/\s+/).filter((w: string) => w.length > 0);
            const min_words =
              this.current_question?.question_options
                ?.allowed_characters?.[0] || 1;
            this.answer_words_count = words.length;
            this.disabled_next_question = !(words.length >= min_words);
          }
        }
      },
      immediate: true
    },
    restored_state: {
      handler(newVal) {
        if (
          newVal &&
          (newVal.current_question_index !== undefined || newVal.answers)
        ) {
          this.restoreFromSavedState();
        }
      },
      deep: true
    },
    // Emit state changes for persistence
    current_question_index() {
      this.emitStateChange();
    }
  },
  mounted() {
    // Restore from saved state if available
    if (this.restored_state) {
      this.restoreFromSavedState();
    } else {
      this.check_stage();
    }

    // Set current question
    this.current_question =
      this.app_eng_assessments.written[this.current_question_index];
    if (this.quill) {
      this.quill.root.addEventListener("copy", (e: ClipboardEvent) => {
        e.preventDefault();
      });
      this.quill.root.addEventListener("cut", (e: ClipboardEvent) => {
        e.preventDefault();
      });
      this.quill.root.addEventListener("paste", (e: ClipboardEvent) => {
        e.preventDefault();
      });
    }
    window.addEventListener("beforeunload", this.handlePageRefresh);
  },
  methods: {
    ...mapMutations("candidate", {
      update_app_eng_assessment_questions:
        ASSESSMENT_QUESTIONNAIRES_ENGLISH_UPDATE,
      set_app_eng_assessments: ASSESSMENT_QUESTIONNAIRES_ENGLISH,
      update_assessment_completed: APP_ENG_ASSESSMENT_COMPLETED
    }),
    ...mapMutations({
      set_root_error: ROOT_ERROR
    }),
    ...mapActions("candidate", {
      submit_assessment_answers: SUBMIT_APP_ASSESSMENT_ANSWERS
    }),
    parseDirective(
      text: string
    ): null | { type: "sentences" | "words"; min: number; max: number } {
      const paren = /\(([^)]*)\)/.exec(text)?.[1] || "";
      const lower = paren.toLowerCase();
      const sRange = lower.match(/(\d+)\s*-\s*(\d+)\s*sentences?/);
      if (sRange) {
        const a = parseInt(sRange[1], 10);
        const b = parseInt(sRange[2], 10);
        const min = Math.min(a, b) || 1;
        const max = Math.max(a, b) || min;
        return { type: "sentences", min, max };
      }
      const sSingle = lower.match(/(\d+)\s*sentences?/);
      if (sSingle) {
        const n = parseInt(sSingle[1], 10) || 1;
        return { type: "sentences", min: n, max: n };
      }
      const wRange = lower.match(/(\d+)\s*-\s*(\d+)\s*words?/);
      if (wRange) {
        const a = parseInt(wRange[1], 10);
        const b = parseInt(wRange[2], 10);
        const min = Math.min(a, b) || 1;
        const max = Math.max(a, b) || min;
        return { type: "words", min, max };
      }
      const wSingle = lower.match(/(\d+)\s*words?/);
      if (wSingle) {
        const n = parseInt(wSingle[1], 10) || 1;
        return { type: "words", min: n, max: n };
      }
      return null;
    },
    /**
     * Restore state from saved session
     */
    restoreFromSavedState() {
      if (!this.restored_state) return;

      if (this.restored_state.current_question_index !== undefined) {
        this.current_question_index =
          this.restored_state.current_question_index;
      }
      if (this.restored_state.answers) {
        // Restore current answer if it exists for current question
        const currentAnswer =
          this.restored_state.answers[this.current_question_index];
        if (currentAnswer) {
          this.current_question_answer = currentAnswer;
        }
      }

      console.log("Written section state restored");
    },
    /**
     * Emit current state for persistence
     */
    emitStateChange() {
      this.$emit("state_changed", {
        section: "written",
        state: {
          current_question_index: this.current_question_index,
          current_question_answer: this.current_question_answer,
          answers: {
            [this.current_question_index]: this.current_question_answer
          }
        }
      });
    },
    async check_stage() {
      const stage = localStorage.getItem("stage");
      console.log("stage", this.app_eng_assessments);
      // assessment_type is now a prop, no need to set it
      console.log(this.assessment_type);
      if (stage && stage === "WRITING") {
        console.log("set_app_eng_assessments13");

        this.set_app_eng_assessments({
          speaking: this.app_eng_assessments.speaking,
          speaking_completed: this.app_eng_assessments.speaking_completed,
          written: this.app_eng_assessments.written,
          filter: this.app_eng_assessments.filter,
          reading_questions: this.app_eng_assessments.reading_questions,
          cancelled: false,
          completed: false,
          current_question_index: this.current_question_index,
          id: this.app_eng_assessments.id,
          assessment_type: this.app_eng_assessments.assessment_type
        });

        this.current_question_answer = localStorage.getItem("answer") || "";
        console.log("current_question_answer", this.current_question_answer);
        // localStorage.removeItem("stage");
        // localStorage.removeItem("stquestion_indexage");
      }
    },
    handlePageRefresh() {
      // Save a check in localStorage on page refresh

      localStorage.removeItem("question_id");
      localStorage.removeItem("answers");

      localStorage.setItem("stage", this.app_eng_assessments.filter);
      localStorage.setItem(
        "question_id",
        this.current_question?.id?.toString() || "null"
      );
      localStorage.setItem("answer", this.current_question_answer);
    },
    convert_seconds_to_minutes,
    // Process next question and set current question to next question if exist
    async process_next_question() {
      // console.log("-----------current_question", this.current_question);
      this.submit_ans_loading = true; // Set submit answer loading
      if (!this.current_question) return; // If current question not exist then return
      // Update current question answer in store
      const payload: UpdateAssessmentQuestionPayload = {
        filter: this.app_eng_assessments.filter,
        answer: this.current_question_answer,
        question_id: this.current_question.id
      };
      this.$emit("question_answered", this.current_question.id);
      window.scrollTo({
        top: 0,
        behavior: "smooth" // optional, smooth scroll
      });
      this.update_app_eng_assessment_questions(payload); // Update current question answer in store
      // Api Call to save current question answer
      const api_payload: SubmitAssessmentAnswersApiPayload = {
        answer: this.current_question_answer,
        question_id: this.current_question.id,
        answer_type: this.current_question.question_type,
        assessment_id: this.current_question.assessment_id
      };
      // Set done to true only when this is the last question AND there is no speaking section after.
      // For english_v4, english_v5, english_genx_easy_v1 the speaking section follows; ML must be triggered only after the last speaking answer.
      const hasSpeakingAfter =
        this.app_eng_assessments.assessment_type === "english_v4" ||
        this.app_eng_assessments.assessment_type === "english_v5" ||
        this.app_eng_assessments.assessment_type === "english_genx_easy_v1" ||
        this.app_eng_assessments.assessment_type === "english_genx_medium_v1" ||
        this.app_eng_assessments.assessment_type === "english_technician_v1" ||
        this.app_eng_assessments.assessment_type ===
          "english_lead_technician_v1" ||
        this.app_eng_assessments.assessment_type ===
          "english_assistant_engineer_v1";
      const isLastWritten =
        this.current_question_index ===
        this.app_eng_assessments.written.length - 1;
      // #region agent log
      fetch(
        "http://127.0.0.1:7243/ingest/13206365-9101-46c2-a8d8-ea23c925c398",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Debug-Session-Id": "d2ab8d"
          },
          body: JSON.stringify({
            sessionId: "d2ab8d",
            runId: "pre-fix",
            hypothesisId: "H3",
            location: "AppEngWrittenAssessment.vue:process_next_question",
            message: "Processing written question",
            data: {
              assessment_type: this.app_eng_assessments.assessment_type,
              hasSpeakingAfter,
              isLastWritten,
              current_question_index: this.current_question_index,
              total_written: this.app_eng_assessments.written.length
            },
            timestamp: Date.now()
          })
        }
      ).catch(() => {
        // Log not critical
      });
      // #endregion agent log
      if (isLastWritten && !hasSpeakingAfter) {
        api_payload.done = true;
      }
      const resposne = await this.submit_assessment_answers(api_payload);
      // If response not exist then set root error and return
      if (!resposne) {
        this.set_root_error(
          "Something went wrong while submitting assessment answers"
        );
        return;
      }
      // If current question index is less than total questions length then set current question to next question
      if (
        this.current_question_index <
        this.app_eng_assessments.written.length - 1
      ) {
        this.current_question_index++; // Increment current question index
        this.current_question_answer = ""; // Reset current question answer
        this.answer_words_count = 0; // Reset current question answer words count
        this.current_question =
          this.app_eng_assessments.written[this.current_question_index]; // Set current question to next question
      } else {
        console.log("set_app_eng_assessments4");
        // v4, v5, genx_easy_v1: written is followed by speaking – only emit so Grammar/Root switches to Speaking; do not set completed or done
        if (
          this.app_eng_assessments.assessment_type === "english_v4" ||
          this.app_eng_assessments.assessment_type === "english_v5" ||
          this.app_eng_assessments.assessment_type === "english_genx_easy_v1" ||
          this.app_eng_assessments.assessment_type ===
            "english_genx_medium_v1" ||
          this.app_eng_assessments.assessment_type ===
            "english_technician_v1" ||
          this.app_eng_assessments.assessment_type ===
            "english_lead_technician_v1" ||
          this.app_eng_assessments.assessment_type ===
            "english_assistant_engineer_v1"
        ) {
          this.$emit("assessment_completed");
          this.submit_ans_loading = false;
          return;
        }

        // If current question index is last question index then set assessment completed to true
        this.set_app_eng_assessments({
          speaking: this.app_eng_assessments.speaking,
          speaking_completed: this.app_eng_assessments.speaking_completed,
          written: this.app_eng_assessments.written,
          reading_questions: this.app_eng_assessments.reading_questions,
          filter: this.app_eng_assessments.filter,
          cancelled: false,
          completed: true,
          current_question_index: this.current_question_index,
          id: this.app_eng_assessments.id,
          assessment_type: this.app_eng_assessments.assessment_type
        });
        localStorage.removeItem("stage");
        localStorage.removeItem("question_id");
        localStorage.removeItem("answer");
        this.$emit("assessment_completed"); // Emit assessment completed event
      }
      this.submit_ans_loading = false; // Set submit answer loading
    },
    get_desired_color(index: number) {
      switch (index) {
        case 0:
          return "green";
        case 1:
          return "yellow";
        case 2:
          return "danger";
        default:
          return "secondary";
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    event_cancellation(event: any) {
      event.preventDefault();
    }
  },
  beforeDestroy() {
    // Remove the event listener to avoid memory leaks
    window.removeEventListener("beforeunload", this.handlePageRefresh);
  }
});
